import { AlertTriangle, Globe, Laptop, RefreshCw, ServerCog, ShieldAlert, ShieldCheck, ShieldQuestion, WifiOff } from 'lucide-react';
import { useWazuhSummary, useDevices } from '../hooks/useWazuh';
import { useSecurityAlerts } from '../hooks/useSecurityAlerts';
import { useVulnScanSummary, useRunVulnScan } from '../hooks/useVulnScan';
import { StatCard } from '../components/jira/StatCard';
import { AgentStatusRing } from '../components/wazuh/AgentStatusRing';
import { AgentTable } from '../components/wazuh/AgentTable';
import { SecurityAlertsList } from '../components/security/SecurityAlertsList';
import { VulnScanReport } from '../components/security/VulnScanReport';
import { DeviceInventoryTable } from '../components/security/DeviceInventoryTable';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/Card';
import { Badge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Spinner } from '../components/ui/Spinner';
import { getApiErrorMessage, isApiError } from '../api/client';
import { formatRelativeTime } from '../utils/format';
import { ComingSoon } from './ComingSoon';

export function Security() {
  const wazuh = useWazuhSummary();
  const alerts = useSecurityAlerts();
  const vulnScan = useVulnScanSummary();
  const runScan = useRunVulnScan();
  const devices = useDevices();

  const wazuhNotConfigured = isApiError(wazuh.error) && wazuh.error.response.status === 503;

  if (wazuh.isLoading && alerts.isLoading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Spinner size={28} />
      </div>
    );
  }

  if (wazuhNotConfigured && !alerts.data) {
    return <ComingSoon icon={ShieldAlert} title="Security overview" integrationLabel="Wazuh" />;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-slate-400">
          {wazuh.data ? `Live from Wazuh \u00b7 updated ${formatRelativeTime(wazuh.data.fetchedAt)}` : 'Server and website security'}
        </p>
        <button
          onClick={() => {
            wazuh.refetch();
            alerts.refetch();
          }}
          className="flex items-center gap-1.5 text-sm font-medium text-pulse-600 hover:text-pulse-700 dark:text-pulse-400"
        >
          <RefreshCw size={14} className={wazuh.isFetching || alerts.isFetching ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      <div>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 dark:text-slate-400">
            <ShieldQuestion size={14} /> Vulnerability scan
          </h2>
          <div className="flex items-center gap-2">
            {vulnScan.data?.criticalFindings ? (
              <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                {vulnScan.data.criticalFindings} high/critical finding{vulnScan.data.criticalFindings === 1 ? '' : 's'}
              </span>
            ) : null}
            <Button size="sm" variant="secondary" onClick={() => runScan.mutate()} disabled={runScan.isPending}>
              <RefreshCw size={13} className={runScan.isPending ? 'animate-spin' : ''} />
              {runScan.isPending ? 'Scanning...' : 'Run scan now'}
            </Button>
          </div>
        </div>
        <Card>
          <CardBody>
            {vulnScan.isLoading ? (
              <div className="flex justify-center py-6">
                <Spinner size={20} />
              </div>
            ) : (
              <VulnScanReport
                sites={vulnScan.data?.sites ?? []}
                server={vulnScan.data?.server ?? null}
                wpVulnDbConfigured={vulnScan.data?.wpVulnDbConfigured ?? false}
              />
            )}
            {vulnScan.data?.lastScanAt && (
              <p className="mt-4 text-xs text-slate-400">
                Scans run every {vulnScan.data.scanIntervalHours}h automatically &middot; last run{' '}
                {formatRelativeTime(vulnScan.data.lastScanAt)}
              </p>
            )}
          </CardBody>
        </Card>
      </div>

      <div>
        <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-500 dark:text-slate-400">
          <Globe size={14} /> Website security alerts
        </h2>
        <Card>
          <CardHeader>
            <CardTitle>Recent alerts</CardTitle>
            {alerts.data && alerts.data.criticalLast24h > 0 && (
              <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-semibold text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                {alerts.data.criticalLast24h} critical in last 24h
              </span>
            )}
          </CardHeader>
          <CardBody>
            {alerts.isLoading ? (
              <div className="flex justify-center py-6">
                <Spinner size={20} />
              </div>
            ) : (
              <SecurityAlertsList alerts={alerts.data?.alerts ?? []} />
            )}
          </CardBody>
        </Card>
      </div>

      <div>
        <h2 className="mb-3 flex items-center gap-1.5 text-sm font-semibold text-slate-500 dark:text-slate-400">
          <ShieldAlert size={14} /> Wazuh (servers & endpoints)
        </h2>
        {wazuh.isLoading ? (
          <Card>
            <CardBody className="flex justify-center py-8">
              <Spinner size={20} />
            </CardBody>
          </Card>
        ) : wazuhNotConfigured ? (
          <Card className="p-6 text-center text-sm text-slate-400">
            Wazuh isn&apos;t configured - set <code className="rounded bg-slate-100 px-1 py-0.5 dark:bg-slate-800">WAZUH_*</code> in
            backend/.env.
          </Card>
        ) : wazuh.isError || !wazuh.data ? (
          <Card className="p-6 text-center">
            <AlertTriangle className="mx-auto mb-2 text-rose-500" size={22} />
            <p className="text-sm font-medium text-slate-700 dark:text-slate-200">Couldn&apos;t load Wazuh data</p>
            <p className="mt-1 text-xs text-slate-400">{getApiErrorMessage(wazuh.error)}</p>
          </Card>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              <StatCard label="Active agents" value={wazuh.data.agentSummary.active} icon={ShieldCheck} tone="success" index={0} />
              <StatCard label="Disconnected" value={wazuh.data.agentSummary.disconnected} icon={WifiOff} tone="danger" index={1} />
              <StatCard label="Pending" value={wazuh.data.agentSummary.pending} icon={AlertTriangle} tone="warning" index={2} />
              <StatCard label="Never connected" value={wazuh.data.agentSummary.neverConnected} icon={ShieldAlert} index={3} />
            </div>

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <Card className="lg:col-span-1">
                <CardHeader>
                  <CardTitle>Agent fleet</CardTitle>
                </CardHeader>
                <CardBody>
                  <AgentStatusRing summary={wazuh.data.agentSummary} />
                </CardBody>
              </Card>

              <Card className="lg:col-span-2">
                <CardHeader className="items-start">
                  <div>
                    <CardTitle>Manager</CardTitle>
                    <p className="mt-1 text-lg font-semibold text-slate-900 dark:text-white">Wazuh {wazuh.data.managerInfo.version}</p>
                  </div>
                  <Badge tone={wazuh.data.managerInfo.status === 'running' ? 'success' : 'danger'}>
                    <ServerCog size={12} className="mr-1 inline" />
                    {wazuh.data.managerInfo.status}
                  </Badge>
                </CardHeader>
                <CardBody>
                  <p className="text-sm text-slate-400">
                    {wazuh.data.agentSummary.total} agents enrolled &middot; {wazuh.data.agentSummary.active} reporting healthy
                  </p>
                </CardBody>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle>Needs attention</CardTitle>
              </CardHeader>
              <CardBody>
                <AgentTable agents={wazuh.data.attentionAgents} />
              </CardBody>
            </Card>
          </div>
        )}
      </div>

      {!wazuhNotConfigured && (
        <div>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="flex items-center gap-1.5 text-sm font-semibold text-slate-500 dark:text-slate-400">
              <Laptop size={14} /> Devices
            </h2>
            {devices.data && devices.data.possiblyUnpatchedCount > 0 && (
              <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                {devices.data.possiblyUnpatchedCount} possibly unpatched
              </span>
            )}
          </div>
          <Card>
            <CardBody>
              <p className="mb-3 text-xs text-slate-400">
                Any machine running the Wazuh agent, not just servers - install it on laptops to see them here too.
              </p>
              {devices.isLoading ? (
                <div className="flex justify-center py-6">
                  <Spinner size={20} />
                </div>
              ) : devices.isError ? (
                <p className="py-4 text-center text-sm text-slate-400">Couldn&apos;t load device inventory.</p>
              ) : (
                <DeviceInventoryTable devices={devices.data?.devices ?? []} />
              )}
            </CardBody>
          </Card>
        </div>
      )}
    </div>
  );
}
